import React, { useEffect, useState, useMemo } from "react";
import styled from "@emotion/styled";
import { SmallText, Spacer } from "../../components/core/utility";
import { Loader } from "@vds/loaders";
import { Tooltip } from "primereact/tooltip";
import FailedIcon from "../../assets/icons/ErrorIcon.svg";
import { Icon } from "@vds/icons";
import { Accordion, AccordionTab } from "primereact/accordion";

/*
  AssignMaterialVehicleGrid
  Requirements per user story:
  - Group by Bundle -> Shipping Address (Ship Group) similar to AssignHardware (without top header info row)
  - Show vehicle info (YMM, VIN, Source, Hardware Priority with prefix 'Hardware', Availability placeholder, Status)
  - Show nested hardware/material mapping columns with:
      Category, Sub-category, Subscription (name, ID), Tech Ref ID, Line Type, Hardware (name, ID), Work Ticket, Work Ticket Status, Ticket Type
*/

const BundleWrapper = styled.div`
  margin-top: 1rem;
`;
const BundleHeader = styled.div`
  background: #000;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 2rem;
`;
const VehicleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 12px;
  table-layout: fixed;
  th,
  td {
    padding: 4px 6px;
    vertical-align: top;
  }
  th {
    background: #f8f9fa;
    font-weight: 600;
    text-align: left;
  }
  tbody tr:nth-of-type(even) {
    background: #fafafa;
  }
`;
const VehicleCell = styled.td`
  font-weight: normal;
`;
const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
`;
const IconWrapper = styled.div`
  height: 18px;
  width: 18px;
  align-items: center;
  cursor: pointer;
  display: flex;
  margin-left: 2px;
  margin-bottom: 0;
  padding: 0;
`;
const TextWrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`;

// NEW: style override to position accordion toggle icon at end
const AccordionStyleOverride = styled.div`
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header:hover .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link {
    color: #000000;
    padding: 0.5rem;
    background: lightgray;
    border-radius: 10px !important;
    margin-bottom: 0.6rem;
    font-family: Verizon-NHG-eDS;
    font-size: 0.8rem;
    font-weight: 500;
    display: flex;
    align-items: center;
  }
  .p-accordion .p-accordion-content {
    padding: 0rem;
    padding-bottom: 0.8rem;
  }
  .p-accordion .p-accordion-header .p-accordion-toggle-icon {
    order: 2;
    margin-left: auto;
  }
`;

export const AssignMaterialVehicleGrid = ({
  bundleIds = [], // array of bundleIds to fetch
  addressId = "",
  contactId = "",
  crmRefId = "",
  materialFilter = {}, // { hardwareId, subscriptionId, techRefId }
  vehicleList = [], // externally supplied vehicles (override fetch)
  activeBundleId = "", // bundle id for externally supplied list
  materialKey = "", // NEW
  externalSelectedVehicleIds = [], // NEW
  onSelectionChange = () => {}, // NEW
  selectionLocked = false, // NEW: when true disable checkboxes & toggling
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vehiclesByBundle, setVehiclesByBundle] = useState({});
  // Track selected vehicles
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [activeAddressIndex, setActiveAddressIndex] = useState({}); // NEW: accordion active index per bundle
  let cspAccountDetails = null;
  cspAccountDetails = sessionStorage && sessionStorage.getItem("CSPDetails") ? JSON.parse(sessionStorage.getItem("CSPDetails")) : null;
  // Build a BundleId -> BundleName map (first occurrence only)
  const bundleNameMap = useMemo(() => {
    const map = {};
    const products = cspAccountDetails?.Product || [];
    products.forEach((p) => {
      if (p?.BundleId && map[p.BundleId] === undefined) {
        map[p.BundleId] = p.BundleName || p.BundleId;
      }
    });
    return map;
  }, [cspAccountDetails]);
  const resolveBundleDisplay = (bundleId) => {
    const name = bundleNameMap[bundleId];
    return name ? `${name} (${bundleId})` : bundleId;
  };
  useEffect(() => {
    // Sync incoming controlled selection
    setSelectedVehicleIds(new Set(externalSelectedVehicleIds || []));
  }, [materialKey, externalSelectedVehicleIds]);

  // NEW helper for consistent unique key generation (single place to adjust if backend shape changes)
  const getVehicleKey = (selVehicle) =>
    selVehicle?.id ||
    selVehicle?.vehicle ||
    (selVehicle
      ? `${selVehicle.year || "?"}-${selVehicle.make || "?"}-${selVehicle.model || "?"}-${selVehicle.vin || Math.random()}`
      : String(Math.random()));

  const toggleVehicleSelected = (vehicle) => {
    if (selectionLocked) return; // prevent changes when locked
    const vehicleKey = getVehicleKey(vehicle); // simplified single-line key generation
    setSelectedVehicleIds((previousSelected) => {
      const updatedSelected = new Set(previousSelected);
      if (updatedSelected.has(vehicleKey)) {
        updatedSelected.delete(vehicleKey);
      } else {
        updatedSelected.add(vehicleKey);
      }
      // Bubble up selection change
      onSelectionChange({
        materialKey,
        hardwareId: materialFilter.hardwareId,
        subscriptionId: materialFilter.subscriptionId,
        techRefId: materialFilter.techRefId,
        vehicleIds: Array.from(updatedSelected),
      });
      return updatedSelected;
    });
  };

  useEffect(() => {
    // New: if vehicleList is an object keyed by bundleId (API shape), use directly
    if (vehicleList && !Array.isArray(vehicleList) && Object.keys(vehicleList).length) {
      setVehiclesByBundle(vehicleList);
      setLoading(false);
      setError("");
      return; // skip further processing
    }
    // If external vehicleList provided as flat array, group by bundleId
    if (Array.isArray(vehicleList) && vehicleList.length) {
      const grouped = {};
      vehicleList.forEach((v) => {
        const bId = v.bundleId || activeBundleId || bundleIds[0] || "";
        if (!grouped[bId]) grouped[bId] = [];
        grouped[bId].push(v);
      });
      setVehiclesByBundle(grouped);
      setLoading(false);
      setError("");
      return; // skip API fetch
    }
    if (!bundleIds.length) return;
    // const fetchData = async () => {
    //   try {
    //     setLoading(true);
    //     setError("");
    //     const joined = bundleIds.join(",");
    //     const resp = await ViewVehicleList(joined, addressId, contactId, crmRefId);
    //     if (resp?.status === 200 && resp?.data?.data?.vehicles) {
    //       const raw = resp.data.data.vehicles;
    //       const filtered = {};
    //       Object.keys(raw).forEach((bid) => {
    //         const vArr = raw[bid] || [];
    //         filtered[bid] = vArr.filter((v) => {
    //           const mm = v.materialMapping || [];
    //           return mm.some(
    //             (m) =>
    //               (!materialFilter.hardwareId || String(m.hardwareId) === String(materialFilter.hardwareId)) &&
    //               (!materialFilter.subscriptionId || String(m.subscriptionId) === String(materialFilter.subscriptionId)) &&
    //               (!materialFilter.techRefId || String(m.techRefId) === String(materialFilter.techRefId))
    //           );
    //         });
    //       });
    //       setVehiclesByBundle(filtered);
    //     } else {
    //       setError("No vehicle data returned");
    //       setVehiclesByBundle({});
    //     }
    //   } catch (e) {
    //     setError("Failed to load vehicle data");
    //     setVehiclesByBundle({});
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    // fetchData();
  }, [
    vehicleList,
    activeBundleId,
    bundleIds,
    addressId,
    contactId,
    crmRefId,
    materialFilter.hardwareId,
    materialFilter.subscriptionId,
    materialFilter.techRefId,
  ]);

  // useEffect(() => {
  //   // New: auto-open first accordion (first bundle's first address group) on initial load
  //   if (Object.keys(vehiclesByBundle).length) {
  //     const firstBundleId = Object.keys(vehiclesByBundle)[0];
  //     if (activeAddressIndex[firstBundleId] === undefined) {
  //       const firstArr = vehiclesByBundle[firstBundleId];
  //       if (Array.isArray(firstArr) && firstArr.length) {
  //         setActiveAddressIndex((prev) => ({ ...prev, [firstBundleId]: 0 }));
  //       }
  //     }
  //   }
  // }, [vehiclesByBundle, activeAddressIndex]);

  const handleAddressAccordionChange = (bundleId) => (e) => {
    setActiveAddressIndex((prev) => ({ ...prev, [bundleId]: e.index }));
  };

  const renderVehicleCell = (row) => {
    const priorityLabel = row.priority ? `Hardware P${row.priority}` : "Hardware --";
    const hasMappings = Array.isArray(row.materialMapping) && row.materialMapping.length > 0;
    const allEnabled = hasMappings && row.materialMapping.every((m) => m.enabled !== false);
    const availabilityLabel = hasMappings && allEnabled ? "Hardware All" : "Missing Hardware";
    return (
      <div>
        <SmallText>
          {row.year} {row.make} {row.model}
        </SmallText>
        {row.displayVinId && <SmallText bold>{row.vehicle}</SmallText>}
        <SmallText css={{ marginTop: "4px" }} bold>
          {row.sourceApplication}
        </SmallText>
        <SmallText css={{ marginTop: "2px" }}>{priorityLabel}</SmallText>
        <SmallText css={{ marginTop: "2px" }}>{availabilityLabel}</SmallText>
        {row.isShippingBlocked && <SmallText css={{ marginTop: "2px" }}>Do Not Ship</SmallText>}
      </div>
    );
  };

  const renderTraits = (row) => (
    <div>
      <div>
        {row.wheelChair && (
          <Icon
            className="trash-icon"
            aria-label="InternationalSymbolOfAccess"
            alt="InternationalSymbolOfAccess"
            name="international-symbol-of-access"
            size="XLarge"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
            color="#000000"
          />
        )}
        {row.emergency && (
          <Icon
            className="police-fleet"
            aria-label="police-fleet"
            alt="police-fleet"
            name="police-fleet"
            size="Large"
            color="#000000"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
          />
        )}
        {row.killSwitch && (
          <Icon
            className="smart-switch"
            aria-label="smart-switch"
            alt="smart-switch"
            name="smart-switch"
            size="Large"
            color="#000000"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
          />
        )}
      </div>
      <SmallText>{row.weightClass}</SmallText>
    </div>
  );

  if (loading) return <Loader active />;
  if (error && !Object.keys(vehiclesByBundle).length) return <SmallText>{error}</SmallText>;
  if (!loading && vehicleList === null) {
    return <SmallText>No data to display</SmallText>;
  }

  return (
    <>
      {Object.keys(vehiclesByBundle).map((bundleId) => {
        const dataArr = vehiclesByBundle[bundleId];
        if (!Array.isArray(dataArr) || !dataArr.length) return null;
        const isAddressGroupShape = dataArr.some((item) => item && typeof item === "object" && Array.isArray(item.vehicles));

        if (isAddressGroupShape) {
          return (
            <BundleWrapper key={bundleId}>
              <BundleHeader>{resolveBundleDisplay(bundleId)}</BundleHeader>
              <AccordionStyleOverride>
                <Accordion
                  expandIcon="pi pi-plus"
                  collapseIcon="pi pi-minus"
                  activeIndex={activeAddressIndex[bundleId]}
                  onTabChange={handleAddressAccordionChange(bundleId)}
                >
                  {dataArr.map((group, gIdx) => {
                    const address = group.address || {};
                    const contact = group.contact || {};
                    const vehicles = Array.isArray(group.vehicles) ? group.vehicles : [];
                    const contactName = contact.name || contact.contact || contact.contactName || "";
                    // Prefer 'address' field from API, fallback to 'address1'
                    const streetLine = address.address || address.address1 || "";
                    const cityPart = address.city ? `, ${address.city}` : "";
                    const statePart = address.stateCode || address.state ? `, ${address.stateCode || address.state}` : "";
                    const zipPart = address.zipcode || address.zipCode ? ` ${address.zipcode || address.zipCode}` : "";
                    const addressLabel = streetLine ? `${streetLine}${cityPart}${statePart}${zipPart}` : `Address-${gIdx + 1}`;
                    return (
                      <AccordionTab
                        key={gIdx}
                        header={
                          <div style={{ fontSize: "14px" }}>
                            <b>{addressLabel}</b>
                            {contactName && (
                              <span>
                                {" - "}
                                <b>{contactName}</b>
                              </span>
                            )}
                          </div>
                        }
                      >
                        <VehicleTable>
                          <tbody>
                            <tr style={{ background: "#f8f9fa" }}>
                              <td style={{ width: "4%" }}></td>
                              <td style={{ width: "18%" }}>
                                <SmallText bold>Vehicle</SmallText>
                              </td>
                              <td style={{ width: "6%" }}>
                                <SmallText bold>Traits</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Category</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Sub-Category</SmallText>
                              </td>
                              <td style={{ width: "14%" }}>
                                <SmallText bold>Subscription</SmallText>
                              </td>
                              <td style={{ width: "13%" }}>
                                <SmallText bold>Tech-Ref Id</SmallText>
                              </td>
                              <td style={{ width: "7%" }}>
                                <SmallText bold>Line Type</SmallText>
                              </td>
                              <td style={{ width: "15%" }}>
                                <SmallText bold>Hardware</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Work Ticket</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Work Ticket Status</SmallText>
                              </td>
                              <td style={{ width: "7%" }}>
                                <SmallText bold>Ticket Type</SmallText>
                              </td>
                            </tr>
                            {vehicles.length === 0 && (
                              <tr>
                                <td colSpan={13}>
                                  <SmallText>No vehicles for this address</SmallText>
                                </td>
                              </tr>
                            )}
                            {vehicles.map((vehicle, vehicleIndex) => {
                              const materialMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
                              const vehicleKey = getVehicleKey(vehicle);
                              const isEvenRow = vehicleIndex % 2 === 1;
                              const rowBackground = isEvenRow ? "rgb(240,240,240)" : "white";
                              if (!materialMappings.length) {
                                return (
                                  <tr key={vehicleKey} style={{ backgroundColor: rowBackground }}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        aria-label="Select vehicle"
                                        checked={selectedVehicleIds.has(vehicleKey)}
                                        onChange={() => toggleVehicleSelected(vehicle)}
                                        disabled={selectionLocked}
                                      />
                                    </td>
                                    <VehicleCell>{renderVehicleCell(vehicle)}</VehicleCell>
                                    <td>{renderTraits(vehicle)}</td>
                                    <td colSpan={9}>
                                      <SmallText>No hardware mapping</SmallText>
                                    </td>
                                  </tr>
                                );
                              }
                              return materialMappings.map((mapping, mappingIndex) => (
                                <tr key={`${vehicleKey}-${mappingIndex}`} style={{ backgroundColor: rowBackground }}>
                                  {mappingIndex === 0 && (
                                    <>
                                      <td rowSpan={materialMappings.length}>
                                        <input
                                          type="checkbox"
                                          aria-label="Select vehicle"
                                          checked={selectedVehicleIds.has(vehicleKey)}
                                          onChange={() => toggleVehicleSelected(vehicle)}
                                          disabled={selectionLocked}
                                        />
                                      </td>
                                      <VehicleCell rowSpan={materialMappings.length}>{renderVehicleCell(vehicle)}</VehicleCell>
                                      <td rowSpan={materialMappings.length}>{renderTraits(vehicle)}</td>
                                    </>
                                  )}
                                  <td>{mapping.type || "--"}</td>
                                  <td>{mapping.subType || "--"}</td>
                                  <td>{mapping.subscriptionName ? `${mapping.subscriptionName} (${mapping.subscriptionId})` : "--"}</td>
                                  <td>{mapping.techRefId || "--"}</td>
                                  <td>{mapping.lineType || mapping.orderType || "--"}</td>
                                  <td>{mapping.hardwareName ? `${mapping.hardwareName} (${mapping.hardwareId})` : "--"}</td>
                                  <td>{mapping.workticketId || "--"}</td>
                                  <td>{mapping.workticketStatus || "--"}</td>
                                  <td>{mapping.workticketType || "--"}</td>
                                </tr>
                              ));
                            })}
                          </tbody>
                        </VehicleTable>
                      </AccordionTab>
                    );
                  })}
                </Accordion>
              </AccordionStyleOverride>
              <Spacer top="1rem" />
            </BundleWrapper>
          );
        }

        const vehs = dataArr;
        const groupedByAddress = vehs.reduce((acc, v) => {
          const addr = v.address || {};
          const key = addr.addressId || addr.address1 || "UNKNOWN";
          if (!acc[key]) acc[key] = { address: addr, vehicles: [] };
          acc[key].vehicles.push(v);
          return acc;
        }, {});
        return (
          <BundleWrapper key={bundleId}>
            <BundleHeader>{resolveBundleDisplay(bundleId)}</BundleHeader>
            <AccordionStyleOverride>
              <Accordion
                expandIcon="pi pi-plus"
                collapseIcon="pi pi-minus"
                activeIndex={activeAddressIndex[bundleId]}
                onTabChange={handleAddressAccordionChange(bundleId)}
              >
                {Object.keys(groupedByAddress).map((addrKey) => {
                  const { address, vehicles } = groupedByAddress[addrKey];
                  const buildAddress = (a) => {
                    const streetLine = a.address || a.address1 || "";
                    if (!streetLine) return addrKey;
                    const parts = [streetLine];
                    const tail = [];
                    if (a.city) tail.push(a.city);
                    if (a.stateCode || a.state) tail.push(a.stateCode || a.state);
                    if (a.zipcode || a.zipCode) tail.push(a.zipcode || a.zipCode);
                    if (tail.length) parts.push(tail.join(", "));
                    return parts.join(", ");
                  };
                  const addressLabel = buildAddress(address);
                  return (
                    <AccordionTab
                      key={addrKey}
                      header={
                        <div style={{ fontSize: "14px" }}>
                          <b>{addressLabel}</b>
                        </div>
                      }
                    >
                      <VehicleTable>
                        <tbody>
                          <tr style={{ background: "#f8f9fa" }}>
                            <td style={{ width: "4%" }}></td>
                            <td style={{ width: "18%" }}>
                              <SmallText bold>Vehicle</SmallText>
                            </td>
                            <td style={{ width: "6%" }}>
                              <SmallText bold>Traits</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Category</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Sub-Category</SmallText>
                            </td>
                            <td style={{ width: "14%" }}>
                              <SmallText bold>Subscription</SmallText>
                            </td>
                            <td style={{ width: "13%" }}>
                              <SmallText bold>Tech-Ref Id</SmallText>
                            </td>
                            <td style={{ width: "7%" }}>
                              <SmallText bold>Line Type</SmallText>
                            </td>
                            <td style={{ width: "15%" }}>
                              <SmallText bold>Hardware</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Work Ticket</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Work Ticket Status</SmallText>
                            </td>
                            <td style={{ width: "7%" }}>
                              <SmallText bold>Ticket Type</SmallText>
                            </td>
                          </tr>
                          {vehicles.map((vehicle, vehicleIndex) => {
                            const materialMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
                            const vehicleKey = getVehicleKey(vehicle);
                            const isEvenVehicleRow = vehicleIndex % 2 === 1;
                            const groupBackgroundColor = isEvenVehicleRow ? "rgb(240, 240, 240)" : "white";
                            if (!materialMappings.length) {
                              return (
                                <tr key={vehicleKey} style={{ backgroundColor: groupBackgroundColor }}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      aria-label="Select vehicle"
                                      checked={selectedVehicleIds.has(vehicleKey)}
                                      onChange={() => toggleVehicleSelected(vehicle)}
                                      disabled={selectionLocked}
                                    />
                                  </td>
                                  <VehicleCell>{renderVehicleCell(vehicle)}</VehicleCell>
                                  <td>{renderTraits(vehicle)}</td>
                                  <td colSpan={9}>
                                    <SmallText>No hardware mapping</SmallText>
                                  </td>
                                </tr>
                              );
                            }
                            return materialMappings.map((mapping, mappingIndex) => (
                              <tr key={`${vehicleKey}-${mappingIndex}`} style={{ backgroundColor: groupBackgroundColor }}>
                                {mappingIndex === 0 && (
                                  <>
                                    <td rowSpan={materialMappings.length}>
                                      <input
                                        type="checkbox"
                                        aria-label="Select vehicle"
                                        checked={selectedVehicleIds.has(vehicleKey)}
                                        onChange={() => toggleVehicleSelected(vehicle)}
                                        disabled={selectionLocked}
                                      />
                                    </td>
                                    <VehicleCell rowSpan={materialMappings.length}>{renderVehicleCell(vehicle)}</VehicleCell>
                                    <td rowSpan={materialMappings.length}>{renderTraits(vehicle)}</td>
                                  </>
                                )}
                                <td>{mapping.type || "--"}</td>
                                <td>{mapping.subType || "--"}</td>
                                <td>{mapping.subscriptionName ? `${mapping.subscriptionName} (${mapping.subscriptionId})` : "--"}</td>
                                <td>{mapping.techRefId || "--"}</td>
                                <td>{mapping.lineType || mapping.orderType || "--"}</td>
                                <td>{mapping.hardwareName ? `${mapping.hardwareName} (${mapping.hardwareId})` : "--"}</td>
                                <td>{mapping.workTicketId || "--"}</td>
                                <td>{mapping.workTicketStatus || mapping.processingCode || "--"}</td>
                                <td>{mapping.ticketType || (mapping.upsell !== undefined ? (mapping.upsell ? "Upsell" : "Standard") : "--")}</td>
                              </tr>
                            ));
                          })}
                        </tbody>
                      </VehicleTable>
                    </AccordionTab>
                  );
                })}
              </Accordion>
            </AccordionStyleOverride>
            <Spacer top="1rem" />
          </BundleWrapper>
        );
      })}
      {Object.keys(vehiclesByBundle).length > 0 && Object.keys(vehiclesByBundle).every((bid) => !vehiclesByBundle[bid]?.length) ? (
        <SmallText>No matching vehicles for selected material</SmallText>
      ) : null}
      {Object.keys(vehiclesByBundle).length === 0 && vehicleList !== null && !loading && <SmallText>No data to display</SmallText>}
    </>
  );
};

export const AssignMaterialVehicleGridMetadata = {
  name: "Assign Material Vehicles",
  id: "assign_material_vehicle_grid",
  component: React.lazy(() => import("./AssignMaterialVehicleGrid")),
  route: "/connect360/assign_material_vehicle_grid",
  Breadcrumbs: true,
};

export default AssignMaterialVehicleGrid;
